import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShoppingCart, CreditCard, Check, Sparkles, Shield, Loader2 } from "lucide-react";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { createPaymentIntent, confirmPayment } from "../services/stripe";

function slugify(name) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/* ─── Credit Card Visual Preview ─── */
function CreditCardPreview({ cardNumber, cardholderName, expiry, cvv, focusedField }) {
  return (
    <div className="relative w-full aspect-[1.586/1] rounded-xl bg-gradient-to-br from-zinc-800 via-zinc-900 to-zinc-950 border border-zinc-700/50 p-5 shadow-2xl overflow-hidden select-none">
      {/* Chip */}
      <div className="absolute top-5 left-5 w-10 h-7 rounded bg-gradient-to-br from-amber-300 to-amber-500 border border-amber-400/30 shadow-inner" />
      {/* Network logo area */}
      <div className="absolute top-5 right-5 flex items-center gap-1.5">
        <div className="w-7 h-5 rounded bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
          <span className="text-[8px] font-bold text-amber-400 tracking-wider">MC</span>
        </div>
      </div>
      {/* Card Number */}
      <div className="absolute left-5 right-5" style={{ top: "42%" }}>
        <div className="font-mono text-lg sm:text-xl tracking-[0.15em] text-white drop-shadow-sm">
          {cardNumber || "XXXX XXXX XXXX XXXX"}
        </div>
      </div>
      {/* Cardholder Name */}
      <div className="absolute left-5 bottom-6">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Cardholder</div>
        <div className={`font-mono text-sm uppercase tracking-wider ${cardholderName ? "text-white" : "text-zinc-500"}`}>
          {cardholderName || "YOUR NAME"}
        </div>
      </div>
      {/* Expiry */}
      <div className="absolute right-20 bottom-6">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Expires</div>
        <div className={`font-mono text-sm ${expiry ? "text-white" : "text-zinc-500"}`}>
          {expiry || "MM/AA"}
        </div>
      </div>
      {/* CVC hint area (small lock icon) */}
      <div className="absolute right-5 bottom-6">
        <div className="flex items-center gap-1 text-zinc-600">
          <Shield className="w-3 h-3" />
        </div>
      </div>
    </div>
  );
}

/* ─── Component ─── */
export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [apiItem, setApiItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState("");
  const [purchased, setPurchased] = useState(false);
  const [purchasedItem, setPurchasedItem] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    cardholderName: "",
    email: "",
    card: "",
    expiry: "",
    cvv: "",
  });

  const agentId = searchParams.get("agentId");
  const skillId = searchParams.get("skillId");

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // Fetch agent or skill from API
  useEffect(() => {
    if (agentId) {
      fetch("/api/marketplace/agents")
        .then((r) => r.json())
        .then((agents) => {
          const agent = agents.find((a) => a.product_id === `agent-${agentId}` || a.product_id === agentId);
          if (agent) {
            setApiItem({
              id: agent.product_id,
              name: agent.name,
              price: agent.price,
              description: agent.description,
              category: "Agent",
              icon: "🤖",
              isSubscription: agent.is_subscription,
              setupFee: agent.setup_fee || 0,
            });
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (skillId) {
      fetch("/api/marketplace/skills")
        .then((r) => r.json())
        .then((skills) => {
          const skill = skills.find((s) => s.product_id === `skill-${skillId}` || s.product_id === skillId);
          if (skill) {
            setApiItem({
              id: skill.product_id,
              name: skill.name,
              price: skill.price,
              description: skill.description,
              category: skill.category || "Skill",
              icon: skill.icon || "🧩",
              author: skill.author || "MaxEmployee",
              isSubscription: skill.is_subscription,
              setupFee: skill.setup_fee || 0,
            });
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [agentId, skillId]);

  const item = apiItem;

  // Confirmation screen
  if (purchased && purchasedItem) {
    return (
      <>
        <SEOHead
          title="Order Confirmed | Max Employee"
          description="Your purchase has been confirmed. Start using your AI agent today."
          url="/order/confirmation"
          noIndex={true}
        />
        <div className="min-h-screen bg-[#09090B] text-white">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 py-20">
            <div className="bg-zinc-950 border border-zinc-900 p-12 text-center rounded-xl">
              <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-amber-500 flex items-center justify-center">
                <Check className="w-10 h-10 text-black" />
              </div>

              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
                // ORDER CONFIRMED
              </div>
              <h1 className="font-display text-4xl uppercase mb-4">
                Thank you, {purchasedItem.buyerName.split(" ")[0]}!
              </h1>
              <p className="text-zinc-400 max-w-md mx-auto leading-relaxed mb-8">
                Your purchase of <span className="text-amber-500 font-semibold">{purchasedItem.name}</span> has been confirmed.
              </p>

              {/* Purchase summary */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-8 text-left max-w-sm mx-auto">
                <h3 className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-4">PURCHASE SUMMARY</h3>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{purchasedItem.icon}</span>
                  <div>
                    <div className="font-bold text-lg">{purchasedItem.name}</div>
                    <div className="text-sm text-zinc-400">{purchasedItem.category}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center border-t border-zinc-800 pt-4">
                  <span className="text-zinc-400">Total</span>
                  <span className="font-display text-2xl text-amber-500">
                    {purchasedItem.isFree ? "Free" : `$${purchasedItem.price.toFixed(2)}`}
                  </span>
                </div>
              </div>

              {/* Email notification badge */}
              <div className="flex items-center justify-center gap-2 text-sm text-zinc-500 mb-8">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span>Email confirmation sent</span>
              </div>

              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={() => navigate("/marketplace")}
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors rounded-lg"
                >
                  Continue Shopping
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-colors rounded-lg"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <SEOHead
          title="Checkout | Max Employee"
          description="Complete your purchase securely."
          url="/checkout"
          noIndex={true}
        />
        <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!item) {
    return (
      <>
        <SEOHead
          title="Item Not Found | Max Employee"
          description="The item you are trying to purchase was not found."
          url="/checkout"
          noIndex={true}
        />
        <div className="min-h-screen bg-[#09090B] text-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </button>
            <div className="text-center py-12">
              <h1 className="text-3xl font-bold mb-4">Item not found</h1>
              <p className="text-zinc-400 mb-8">The agent or skill you are trying to purchase was not found.</p>
              <button
                onClick={() => navigate("/marketplace")}
                className="px-6 py-3 rounded-lg bg-amber-500 text-black font-medium hover:bg-amber-400 transition-colors"
              >
                Back to Marketplace
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const isFree = item.price === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
    if (!isFree) {
      if (!formData.cardholderName.trim()) newErrors.cardholderName = "Name is required";
      if (!formData.card.trim()) newErrors.card = "Card number is required";
      if (!formData.expiry.trim()) newErrors.expiry = "Expiry is required";
      if (!formData.cvv.trim()) newErrors.cvv = "CVV is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setProcessing(true);
    setProcessingStage("Creating payment...");
    setErrors({});

    try {
      const plan = item.id;
      const amount = Math.round(item.price * 100); // Convert to cents

      const { clientSecret, orderId } = await createPaymentIntent(plan, amount);

      // Confirm payment (mock for now)
      setProcessingStage("Confirming payment...");
      const result = await confirmPayment({
        clientSecret,
        orderId,
        paymentMethodId: 'pm_mock_card'
      });

      if (result.success) {
        // Save purchase to localStorage
        const purchases = JSON.parse(localStorage.getItem("maxemployee_purchases") || "[]");
        const purchase = {
          id: Date.now(),
          itemId: item.id,
          itemName: item.name,
          itemIcon: item.icon,
          price: item.price,
          buyerName: formData.cardholderName,
          buyerEmail: formData.email,
          purchasedAt: new Date().toISOString(),
          isFree,
        };
        purchases.push(purchase);
        localStorage.setItem("maxemployee_purchases", JSON.stringify(purchases));

        // Reset onboarding wizard
        localStorage.removeItem("maxemployee.onboarding_wizard_completed");

        setProcessingStage("Redirecting...");
        await new Promise(r => setTimeout(r, 400));

        setPurchased(true);
        setPurchasedItem({ ...item, buyerName: formData.cardholderName, buyerEmail: formData.email });
      } else {
        throw new Error("Payment confirmation failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setErrors({ submit: "Payment failed. Please try again." });
    } finally {
      setProcessing(false);
      setProcessingStage("");
    }
  };

  return (
    <>
      <SEOHead
        title="Checkout | Max Employee"
        description="Complete your purchase securely."
        url="/checkout"
        noIndex={true}
      />
      <div className="min-h-screen bg-[#09090B] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
            // Checkout
          </div>
          <h1 className="font-display text-4xl uppercase mb-8">Complete Purchase</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column: Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal info */}
                <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
                  <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-6">Personal Info</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={`w-full bg-zinc-900 border ${errors.email ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors`}
                      />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                </div>

                {/* Payment info — credit card section */}
                {!isFree && (
                  <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <CreditCard className="w-4 h-4 text-amber-500" />
                      <h2 className="font-mono text-xs uppercase tracking-wider text-zinc-500">Card Payment</h2>
                    </div>

                    {/* Visual credit card preview */}
                    <div className="mb-6 max-w-sm mx-auto">
                      <CreditCardPreview
                        cardNumber={formData.card}
                        cardholderName={formData.cardholderName}
                        expiry={formData.expiry}
                        cvv={formData.cvv}
                      />
                    </div>

                    <div className="space-y-4">
                      {/* Cardholder Name */}
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">Cardholder Name</label>
                        <input
                          type="text"
                          name="cardholderName"
                          value={formData.cardholderName}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className={`w-full bg-zinc-900 border ${errors.cardholderName ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors uppercase`}
                        />
                        {errors.cardholderName && <p className="text-red-400 text-xs mt-1">{errors.cardholderName}</p>}
                      </div>

                      {/* Card Number */}
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">Card Number</label>
                        <input
                          type="text"
                          name="card"
                          value={formData.card}
                          onChange={handleChange}
                          placeholder="0000 0000 0000 0000"
                          maxLength={19}
                          className={`w-full bg-zinc-900 border ${errors.card ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono tracking-wider`}
                        />
                        {errors.card && <p className="text-red-400 text-xs mt-1">{errors.card}</p>}
                      </div>

                      {/* Expiry + CVC side by side */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1.5">Expiry</label>
                          <input
                            type="text"
                            name="expiry"
                            value={formData.expiry}
                            onChange={handleChange}
                            placeholder="MM/AA"
                            maxLength={5}
                            className={`w-full bg-zinc-900 border ${errors.expiry ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono`}
                          />
                          {errors.expiry && <p className="text-red-400 text-xs mt-1">{errors.expiry}</p>}
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1.5">CVV</label>
                          <input
                            type="text"
                            name="cvv"
                            value={formData.cvv}
                            onChange={handleChange}
                            placeholder="000"
                            maxLength={4}
                            className={`w-full bg-zinc-900 border ${errors.cvv ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono`}
                          />
                          {errors.cvv && <p className="text-red-400 text-xs mt-1">{errors.cvv}</p>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-4 text-xs text-zinc-600">
                        <Shield className="w-3.5 h-3.5" />
                        <span>Secure checkout powered by Stripe</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={processing}
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-4 font-bold uppercase tracking-wider transition-colors rounded-lg text-sm disabled:opacity-50"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{processingStage || "Processing..."}</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4" />
                      {isFree ? "Add to Agent" : `Confirm Purchase — $${item.price.toFixed(2)}`}
                    </>
                  )}
                </button>

                {errors.submit && <p className="text-red-400 text-center mt-2">{errors.submit}</p>}
              </form>
            </div>

            {/* Right column: Summary */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6 sticky top-24">
                <h3 className="font-display text-lg uppercase mb-4">Order Summary</h3>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <div className="font-bold text-white">{item.name}</div>
                    <div className="text-sm text-zinc-400">{item.category}</div>
                  </div>
                </div>
                <div className="border-t border-zinc-800 pt-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="font-bold text-white">${item.price.toFixed(2)}</span>
                  </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                  <div className="font-mono text-xs uppercase text-amber-500">TOTAL</div>
                  <div className="font-display text-2xl text-amber-500">
                    {isFree ? "Free" : `$${item.price.toFixed(2)}`}
                  </div>
                </div>
                {!isFree && (
                  <div className="mt-4 text-xs text-zinc-500 text-center">
                    <Shield className="w-3 h-3 inline mr-1" />
                    Secure checkout powered by Stripe
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}